import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import cogoToast from 'cogo-toast';
import { CLEARABLE_COLLECTIONS, clearMultipleCollections } from 'DBServices/firestoreDBService';

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.65);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 16px;
    backdrop-filter: blur(2px);
`;

const Modal = styled.div`
    background: #ffffff;
    border-radius: 16px;
    width: 100%;
    max-width: 680px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    border: 1px solid #f3f4f6;
`;

const Header = styled.div`
    padding: 20px 24px;
    background: linear-gradient(135deg, #dc2626, #b91c1c);
    color: #ffffff;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
`;

const Body = styled.div`
    padding: 24px;
    overflow-y: auto;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const Footer = styled.div`
    padding: 16px 24px;
    background: #f9fafb;
    border-top: 1px solid #e5e7eb;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const PresetBtn = styled.button`
    padding: 6px 12px;
    border-radius: 8px;
    font-size: 0.75rem;
    font-weight: 600;
    border: 1px solid ${props => props.active ? '#1f2937' : '#d1d5db'};
    background: ${props => props.active ? '#1f2937' : '#ffffff'};
    color: ${props => props.active ? '#ffffff' : '#374151'};
    cursor: pointer;
    transition: all 0.15s ease;
    &:hover {
        background: ${props => props.active ? '#111827' : '#f3f4f6'};
    }
`;

const GroupTitle = styled.h4`
    font-size: 0.75rem;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const CollectionCard = styled.label`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    border-radius: 10px;
    border: 1px solid ${props => props.checked ? '#f87171' : '#e5e7eb'};
    background: ${props => props.checked ? '#fef2f2' : '#ffffff'};
    color: ${props => props.checked ? '#7f1d1d' : '#374151'};
    cursor: pointer;
    transition: all 0.15s ease;
    user-select: none;
    margin-bottom: 8px;
    &:hover {
        background: ${props => props.checked ? '#fee2e2' : '#f9fafb'};
    }
`;

const CountBadge = styled.span`
    font-size: 0.75rem;
    padding: 2px 8px;
    border-radius: 9999px;
    font-weight: 700;
    margin-left: 8px;
    white-space: nowrap;
    background: ${props => props.hasData ? '#fee2e2' : '#f3f4f6'};
    color: ${props => props.hasData ? '#b91c1c' : '#6b7280'};
`;

const DangerAlert = styled.div`
    background: #fef2f2;
    border-left: 4px solid #ef4444;
    padding: 14px 16px;
    border-radius: 0 8px 8px 0;
    font-size: 0.78rem;
    color: #991b1b;
    line-height: 1.4;
`;

const ConfirmInput = styled.input`
    width: 100%;
    border: 2px solid #fca5a5;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 0.9rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    color: #1f2937;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.15s ease;
    &:focus {
        border-color: #dc2626;
    }
`;

const DeleteBtn = styled.button`
    padding: 10px 20px;
    border-radius: 10px;
    font-weight: 700;
    font-size: 0.85rem;
    color: #ffffff;
    border: none;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.15s ease;
    background: ${props => props.disabled ? '#d1d5db' : '#dc2626'};
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    opacity: ${props => props.disabled ? 0.65 : 1};
    box-shadow: ${props => props.disabled ? 'none' : '0 2px 4px rgba(220, 38, 38, 0.25)'};
    &:hover {
        background: ${props => props.disabled ? '#d1d5db' : '#b91c1c'};
    }
`;

const CancelBtn = styled.button`
    padding: 10px 18px;
    border-radius: 10px;
    font-weight: 600;
    font-size: 0.85rem;
    color: #4b5563;
    background: #ffffff;
    border: 1px solid #d1d5db;
    cursor: pointer;
    transition: background 0.15s ease;
    &:hover {
        background: #f3f4f6;
    }
`;

export default function AdminClearDataModal({
    isOpen,
    onClose,
    onSuccess,
    initialTarget = null,
    counts = {}
}) {
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [confirmText, setConfirmText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [progressStatus, setProgressStatus] = useState('');
    const [progressLogs, setProgressLogs] = useState([]);

    // Initialize selection when opened
    useEffect(() => {
        if (!isOpen) {
            setConfirmText('');
            setProgressStatus('');
            setProgressLogs([]);
            return;
        }

        if (initialTarget) {
            const match = CLEARABLE_COLLECTIONS.find(c => c.key === initialTarget || c.col === initialTarget);
            if (match) {
                setSelectedKeys([match.key]);
            } else {
                setSelectedKeys(['foods', 'categories', 'locations']);
            }
        } else {
            setSelectedKeys(['foods', 'categories', 'locations']);
        }
    }, [isOpen, initialTarget]);

    if (!isOpen) return null;

    const toggleKey = (key) => {
        if (isDeleting) return;
        setSelectedKeys(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const handleSelectPreset = (preset) => {
        if (isDeleting) return;
        if (preset === 'catalog') {
            setSelectedKeys(['foods', 'categories', 'locations', 'markets', 'featured']);
        } else if (preset === 'all') {
            setSelectedKeys(CLEARABLE_COLLECTIONS.map(c => c.key));
        } else if (preset === 'none') {
            setSelectedKeys([]);
        }
    };

    const groups = [
        { name: 'Catalog & Inventory', icon: '📦' },
        { name: 'Orders & Operations', icon: '💳' },
        { name: 'Content & Inquiries', icon: '📝' }
    ];

    const totalSelectedRecords = selectedKeys.reduce((sum, key) => {
        return sum + (Number(counts[key]) || 0);
    }, 0);

    const isConfirmed = confirmText.trim().toUpperCase() === 'DELETE' && selectedKeys.length > 0;

    const handleExecuteDelete = async () => {
        if (!isConfirmed || isDeleting) return;

        setIsDeleting(true);
        setProgressStatus('Starting deletion process...');
        setProgressLogs([]);

        try {
            const results = await clearMultipleCollections(selectedKeys, (step) => {
                if (step.status === 'in-progress') {
                    const msg = `Clearing ${step.label}...`;
                    setProgressStatus(msg);
                } else if (step.status === 'done') {
                    const msg = `Cleared ${step.label} (${step.count || 0} removed)`;
                    setProgressLogs(prev => [...prev, msg]);
                } else if (step.status === 'error') {
                    const msg = `Failed to clear ${step.label}: ${step.error}`;
                    setProgressLogs(prev => [...prev, msg]);
                }
            });

            cogoToast.success(`Successfully cleared ${selectedKeys.length} dataset(s)!`, { position: 'top-center' });

            if (onSuccess) {
                await onSuccess(selectedKeys, results);
            }

            setTimeout(() => {
                setIsDeleting(false);
                onClose();
            }, 600);
        } catch (err) {
            console.error('Batch clear failed:', err);
            cogoToast.error('An error occurred while clearing data: ' + err.message);
            setIsDeleting(false);
        }
    };

    return (
        <Overlay onClick={() => !isDeleting && onClose()}>
            <Modal onClick={e => e.stopPropagation()}>
                {/* Header */}
                <Header>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span role="img" aria-label="warning" style={{ fontSize: '1.4rem' }}>⚠️</span>
                            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, margin: 0 }}>Clear Database / Demo Data</h3>
                        </div>
                        <p style={{ fontSize: '0.78rem', color: '#fee2e2', marginTop: 4, margin: '4px 0 0 0' }}>
                            Purge demo foods, categories, locations, and other records so you can start fresh with real data.
                        </p>
                    </div>
                    {!isDeleting && (
                        <button
                            onClick={onClose}
                            style={{
                                color: '#ffffff',
                                background: 'transparent',
                                border: 'none',
                                fontSize: '1.5rem',
                                fontWeight: 700,
                                cursor: 'pointer',
                                padding: 4,
                                lineHeight: 1
                            }}
                        >
                            &times;
                        </button>
                    )}
                </Header>

                {/* Body */}
                <Body>
                    {/* Quick Presets */}
                    <div>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#4b5563', marginBottom: 8 }}>
                            Quick Presets:
                        </label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            <PresetBtn
                                type="button"
                                active={selectedKeys.length === 5 && selectedKeys.includes('foods') && selectedKeys.includes('categories') && selectedKeys.includes('locations')}
                                onClick={() => handleSelectPreset('catalog')}
                            >
                                <span role="img" aria-label="salad" style={{ marginRight: 4 }}>🥗</span>
                                Catalog Only (Foods, Categories, Locations...)
                            </PresetBtn>
                            <PresetBtn
                                type="button"
                                active={selectedKeys.length === CLEARABLE_COLLECTIONS.length}
                                onClick={() => handleSelectPreset('all')}
                            >
                                <span role="img" aria-label="wastebasket" style={{ marginRight: 4 }}>🗑️</span>
                                Select All Demo Data
                            </PresetBtn>
                            <PresetBtn
                                type="button"
                                active={selectedKeys.length === 0}
                                onClick={() => handleSelectPreset('none')}
                            >
                                ✖ Clear Selection
                            </PresetBtn>
                        </div>
                    </div>

                    {/* Grouped Checklist */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {groups.map(grp => {
                            const items = CLEARABLE_COLLECTIONS.filter(c => c.group === grp.name);
                            if (items.length === 0) return null;

                            return (
                                <div key={grp.name}>
                                    <GroupTitle>
                                        <span>{grp.icon}</span>
                                        <span>{grp.name}</span>
                                    </GroupTitle>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 8 }}>
                                        {items.map(item => {
                                            const isChecked = selectedKeys.includes(item.key);
                                            const recordCount = counts[item.key] !== undefined ? counts[item.key] : null;

                                            return (
                                                <CollectionCard
                                                    key={item.key}
                                                    checked={isChecked}
                                                    onClick={() => toggleKey(item.key)}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            onChange={() => {}}
                                                            disabled={isDeleting}
                                                            style={{ width: 16, height: 16, accentColor: '#dc2626', cursor: 'pointer' }}
                                                        />
                                                        <div style={{ overflow: 'hidden' }}>
                                                            <div style={{ fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                                                {item.label}
                                                            </div>
                                                            <div style={{ fontSize: '0.68rem', color: '#6b7280', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                                                {item.description}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {recordCount !== null && (
                                                        <CountBadge hasData={recordCount > 0}>
                                                            {recordCount} {recordCount === 1 ? 'item' : 'items'}
                                                        </CountBadge>
                                                    )}
                                                </CollectionCard>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Progress details when deleting */}
                    {isDeleting && (
                        <div style={{ background: '#111827', color: '#34d399', padding: 16, borderRadius: 12, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#ffffff', fontWeight: 700, marginBottom: 8 }}>
                                <span role="img" aria-label="hourglass">⏳</span>
                                <span>{progressStatus}</span>
                            </div>
                            {progressLogs.map((log, idx) => (
                                <div key={idx} style={{ marginTop: 2 }}>✓ {log}</div>
                            ))}
                        </div>
                    )}

                    {/* Danger Alert & Text Confirmation */}
                    {!isDeleting && (
                        <DangerAlert>
                            <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#7f1d1d', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                <span role="img" aria-label="warning">⚠️</span>
                                <span>Irreversible Action</span>
                            </div>
                            <div>
                                Deleting selected data permanently wipes these documents from Firestore. User accounts and app configuration settings are protected.
                            </div>
                            <div style={{ fontWeight: 700, color: '#7f1d1d', marginTop: 6 }}>
                                Selected: <strong>{selectedKeys.length} collection(s)</strong> {totalSelectedRecords > 0 && `(~${totalSelectedRecords} total records)`}.
                            </div>
                        </DangerAlert>
                    )}

                    {!isDeleting && (
                        <div style={{ background: '#fef2f2', padding: 16, borderRadius: 12, border: '1px solid #fecaca' }}>
                            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#7f1d1d', marginBottom: 6 }}>
                                To confirm, please type <span style={{ textDecoration: 'underline', textTransform: 'uppercase', color: '#b91c1c', fontWeight: 900 }}>DELETE</span> in the box below:
                            </label>
                            <ConfirmInput
                                type="text"
                                value={confirmText}
                                onChange={e => setConfirmText(e.target.value)}
                                placeholder="Type DELETE to enable button"
                                autoFocus
                            />
                        </div>
                    )}
                </Body>

                {/* Footer */}
                <Footer>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500 }}>
                        {selectedKeys.length === 0 ? (
                            <span>No datasets selected</span>
                        ) : (
                            <span>{selectedKeys.length} dataset(s) ready to clear</span>
                        )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <CancelBtn
                            type="button"
                            onClick={onClose}
                            disabled={isDeleting}
                        >
                            Cancel
                        </CancelBtn>
                        <DeleteBtn
                            type="button"
                            disabled={!isConfirmed || isDeleting}
                            onClick={handleExecuteDelete}
                        >
                            {isDeleting ? (
                                <span>Deleting Data...</span>
                            ) : (
                                <>
                                    <span role="img" aria-label="wastebasket">🗑️</span>
                                    <span>Permanently Delete Selected ({selectedKeys.length})</span>
                                </>
                            )}
                        </DeleteBtn>
                    </div>
                </Footer>
            </Modal>
        </Overlay>
    );
}
